// PalavraMinutoAdmin — CMS Fase A+B+C (iter67).
//
// Correção do bug de galeria mobile:
//   - Botão principal SEM capture (abre galeria/Arquivos no iOS e Android).
//   - Botão secundário "Gravar agora" com capture (câmera).
//   - accept="video/mp4,video/quicktime,video/*" (aceita MOV do iPhone).
//   - Limite editorial 89 segundos (1min 29s).
//
// Nova origem: link externo (YouTube, YouTube Shorts, MP4 direto HTTPS).
//   - Tabs: "Enviar arquivo" | "Usar link de vídeo"
//   - Validação client-side de URL (HTTPS + padrão YouTube/MP4).
//
// Fluxo simplificado:
//   - Sem ID manual de devocional (seletor amigável, opcional).
//   - Sem status/ordem no formulário (auto-publish pela data).
//   - Publicação automática pela data escolhida.
//
// Backend endpoints:
//   GET    /api/admin/palavra-minuto
//   POST   /api/admin/palavra-minuto              (multipart — upload)
//   POST   /api/admin/palavra-minuto/link         (JSON — externo)
//   PATCH  /api/admin/palavra-minuto/{id}
//   POST   /api/admin/palavra-minuto/{id}/replace (multipart)
//   DELETE /api/admin/palavra-minuto/{id}
//   GET    /api/admin/devotionals-selector        (seletor amigável)

import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Film, Upload, Trash2, RefreshCw, LinkIcon, Camera, Image as ImageIcon, X } from "lucide-react";
import { api } from "../../lib/api";

const MAX_SIZE_BYTES = 60 * 1024 * 1024;
const MAX_DURATION_S = 89;        // Iter67: limite editorial 89s (1min 29s).
const VIDEO_ACCEPT = "video/mp4,video/quicktime,video/*";
const VALID_VIDEO_EXTS = [".mp4", ".mov"];

const SOURCE_UPLOAD = "upload";
const SOURCE_LINK = "link";

// Regex — mesmos formatos aceitos pelo backend.
const YT_PATTERNS = [
  /^https?:\/\/(?:www\.|m\.)?youtube\.com\/watch\?v=([A-Za-z0-9_-]{11})(?:[&?].*)?$/,
  /^https?:\/\/(?:www\.)?youtube\.com\/shorts\/([A-Za-z0-9_-]{11})(?:[?].*)?$/,
  /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([A-Za-z0-9_-]{11})(?:[?].*)?$/,
  /^https?:\/\/youtu\.be\/([A-Za-z0-9_-]{11})(?:[?].*)?$/,
];

function bytesToMB(n) { return (n / (1024 * 1024)).toFixed(1); }
function formatSeconds(s) {
  if (s == null || !Number.isFinite(s)) return "·";
  const total = Math.round(s);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}min ${String(sec).padStart(2, "0")}s`;
}

const STATUS_STYLE = {
  draft: "bg-amber-100 text-amber-700",
  reviewed: "bg-sky-100 text-sky-700",
  published: "bg-emerald-100 text-emerald-700",
  hidden: "bg-navy/10 text-navy/60",
};
const STATUS_LABEL = {
  draft: "Rascunho",
  reviewed: "Revisado",
  published: "Publicado",
  hidden: "Oculto",
};

const EMPTY_FORM = {
  title: "",
  description: "",
  scheduled_date: new Date().toISOString().slice(0, 10),
  devotional_id: "",
};

function classifyLink(url) {
  if (!url || typeof url !== "string") return { valid: false, error: "Informe uma URL." };
  const s = url.trim();
  if (!s.toLowerCase().startsWith("http")) return { valid: false, error: "A URL precisa começar com https://" };
  if (s.toLowerCase().startsWith("http://")) return { valid: false, error: "Apenas HTTPS é aceito (link inseguro)." };
  for (const pat of YT_PATTERNS) {
    const m = s.match(pat);
    if (m) return { valid: true, kind: "youtube", id: m[1] };
  }
  try {
    const parsed = new URL(s);
    if (parsed.pathname.toLowerCase().endsWith(".mp4")) {
      return { valid: true, kind: "mp4" };
    }
  } catch { /* invalid URL */ }
  return { valid: false, error: "Envie um link do YouTube (assista/Shorts) ou uma URL HTTPS que termine em .mp4." };
}

function fileHasValidExt(name) {
  const low = (name || "").toLowerCase();
  return VALID_VIDEO_EXTS.some((e) => low.endsWith(e));
}

async function probeVideo(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      const info = { duration: v.duration, width: v.videoWidth, height: v.videoHeight };
      URL.revokeObjectURL(url);
      resolve(info);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    v.src = url;
  });
}

export default function PalavraMinutoAdmin() {
  const [items, setItems] = useState([]);
  const [devos, setDevos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [source, setSource] = useState(SOURCE_UPLOAD);
  const [videoFile, setVideoFile] = useState(null);
  const [videoInfo, setVideoInfo] = useState(null);
  const [videoError, setVideoError] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [linkInfo, setLinkInfo] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [replacingId, setReplacingId] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const thumbRef = useRef(null);
  const replaceRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/palavra-minuto"),
        api.get("/admin/devotionals-selector").catch(() => ({ data: [] })),
      ]);
      setItems(Array.isArray(r1.data) ? r1.data : []);
      setDevos(Array.isArray(r2.data) ? r2.data : []);
    } catch {
      setItems([]);
      setDevos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const clearVideo = () => {
    setVideoFile(null);
    setVideoInfo(null);
    setVideoError(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (galleryInputRef.current) galleryInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const handleVideoPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { clearVideo(); return; }

    setVideoError(null);
    if (!fileHasValidExt(f.name)) {
      const msg = "Formato não suportado. Envie um vídeo .mp4 (Android) ou .mov (iPhone).";
      setVideoError(msg);
      toast.error(msg);
      e.target.value = "";
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      const msg = `Vídeo com ${bytesToMB(f.size)} MB. O limite é de ${MAX_SIZE_BYTES / (1024 * 1024)} MB. Comprima o arquivo antes de enviar.`;
      setVideoError(msg);
      toast.error(msg);
      e.target.value = "";
      return;
    }
    const info = await probeVideo(f);
    if (!info) {
      const msg = "Não foi possível ler o vídeo. O arquivo pode estar corrompido ou usar um codec incompatível.";
      setVideoError(msg);
      toast.error(msg);
      e.target.value = "";
      return;
    }
    // Aceita a seleção mesmo acima do limite, mas bloqueia o envio depois
    // (para mostrar a duração detectada — feedback melhor que rejeitar cegamente).
    if (preview) URL.revokeObjectURL(preview);
    setVideoFile(f);
    setVideoInfo(info);
    setPreview(URL.createObjectURL(f));
  };

  const handleLinkChange = (val) => {
    setExternalUrl(val);
    setLinkInfo(val ? classifyLink(val) : null);
  };

  const canSubmit = (() => {
    if (uploading || !form.title.trim()) return false;
    if (source === SOURCE_UPLOAD) {
      if (!videoFile || !videoInfo) return false;
      if (videoInfo.duration > MAX_DURATION_S + 0.5) return false;
      return true;
    }
    // link
    return !!(linkInfo && linkInfo.valid);
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Título é obrigatório."); return; }
    setUploading(true);
    try {
      if (source === SOURCE_UPLOAD) {
        if (!videoFile) { toast.error("Selecione um vídeo (MP4 ou MOV)."); return; }
        if (!videoInfo || videoInfo.duration > MAX_DURATION_S + 0.5) {
          toast.error(`Duração ${formatSeconds(videoInfo?.duration)} excede o limite de ${formatSeconds(MAX_DURATION_S)}.`);
          return;
        }
        const fd = new FormData();
        fd.append("title", form.title.trim());
        if (form.description) fd.append("description", form.description);
        if (form.scheduled_date) fd.append("scheduled_date", form.scheduled_date);
        if (form.devotional_id) fd.append("devotional_id", form.devotional_id);
        fd.append("video", videoFile);
        if (thumbFile) fd.append("thumbnail", thumbFile);
        await api.post("/admin/palavra-minuto", fd);
      } else {
        if (!linkInfo || !linkInfo.valid) {
          toast.error(linkInfo?.error || "Link inválido.");
          return;
        }
        await api.post("/admin/palavra-minuto/link", {
          title: form.title.trim(),
          description: form.description || null,
          external_url: externalUrl.trim(),
          scheduled_date: form.scheduled_date || null,
          devotional_id: form.devotional_id || null,
        });
      }
      toast.success("Palavra em 1 Minuto salva.");
      setForm(EMPTY_FORM);
      clearVideo();
      setThumbFile(null);
      setExternalUrl("");
      setLinkInfo(null);
      if (thumbRef.current) thumbRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar. Tente novamente.");
    } finally {
      setUploading(false);
    }
  };

  const changeStatus = async (item, newStatus) => {
    try {
      await api.patch(`/admin/palavra-minuto/${item.id}`, { status: newStatus });
      toast.success(`Status: ${STATUS_LABEL[newStatus] || newStatus}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar status.");
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/admin/palavra-minuto/${id}`);
      toast.success("Vídeo removido.");
      setConfirmDeleteId(null);
      await load();
    } catch {
      toast.error("Falha ao remover.");
    }
  };

  const doReplace = async (id, file) => {
    if (!file) return;
    if (!fileHasValidExt(file.name)) { toast.error("Envie MP4 ou MOV."); return; }
    const info = await probeVideo(file);
    if (!info || info.duration > MAX_DURATION_S + 0.5) {
      toast.error(`Vídeo inválido ou acima de ${formatSeconds(MAX_DURATION_S)}.`);
      return;
    }
    const fd = new FormData();
    fd.append("video", file);
    try {
      await api.post(`/admin/palavra-minuto/${id}/replace`, fd);
      toast.success("Arquivo substituído.");
      setReplacingId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao substituir.");
    }
  };

  const withinLimit = videoInfo ? videoInfo.duration <= MAX_DURATION_S + 0.5 : null;

  return (
    <section className="space-y-6" data-testid="admin-palavra-minuto">
      <header>
        <div className="flex items-center gap-2">
          <Film className="w-5 h-5 text-gold" />
          <h2 className="font-display text-2xl text-navy">Palavra em 1 Minuto</h2>
        </div>
        <p className="text-sm text-navy/65 mt-1 max-w-2xl">
          Vídeo pastoral curto vinculado ao Devocional do Dia. Limite editorial: 1 minuto e 29 segundos ({MAX_DURATION_S}s).
          Envie um arquivo (MP4/MOV) ou use um link do YouTube.
        </p>
      </header>

      <form onSubmit={submit} className="bg-white border border-border rounded-2xl p-5 sm:p-6 space-y-5" data-testid="admin-pm-form">
        {/* Tabs de origem */}
        <div className="inline-flex bg-[hsl(var(--parchment))] rounded-full p-1 gap-1" data-testid="admin-pm-source-tabs">
          <button
            type="button"
            onClick={() => setSource(SOURCE_UPLOAD)}
            data-testid="admin-pm-tab-upload"
            className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors ${
              source === SOURCE_UPLOAD ? "bg-navy text-white" : "text-navy/70 hover:text-navy"
            }`}
          >
            <Upload className="inline w-3.5 h-3.5 mr-1" /> Enviar arquivo
          </button>
          <button
            type="button"
            onClick={() => setSource(SOURCE_LINK)}
            data-testid="admin-pm-tab-link"
            className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors ${
              source === SOURCE_LINK ? "bg-navy text-white" : "text-navy/70 hover:text-navy"
            }`}
          >
            <LinkIcon className="inline w-3.5 h-3.5 mr-1" /> Usar link de vídeo
          </button>
        </div>

        {source === SOURCE_UPLOAD ? (
          <div className="space-y-3">
            <p className="text-xs text-navy/70">
              Escolha um vídeo já salvo no aparelho ou grave agora. Aceitamos <strong>.mp4</strong> e <strong>.mov</strong> (iPhone),
              até {formatSeconds(MAX_DURATION_S)} e {MAX_SIZE_BYTES / (1024 * 1024)} MB.
            </p>

            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-4 py-2.5 rounded-full text-[11px] uppercase tracking-widest cursor-pointer transition-colors" data-testid="admin-pm-btn-gallery">
                <ImageIcon className="w-3.5 h-3.5" /> Selecionar vídeo da galeria
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept={VIDEO_ACCEPT}
                  onChange={handleVideoPick}
                  data-testid="admin-pm-video-input-gallery"
                  className="hidden"
                />
              </label>
              <label className="inline-flex items-center gap-2 border border-navy/25 text-navy hover:border-gold hover:text-gold px-4 py-2.5 rounded-full text-[11px] uppercase tracking-widest cursor-pointer transition-colors" data-testid="admin-pm-btn-camera">
                <Camera className="w-3.5 h-3.5" /> Gravar agora
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept={VIDEO_ACCEPT}
                  capture="environment"
                  onChange={handleVideoPick}
                  data-testid="admin-pm-video-input-camera"
                  className="hidden"
                />
              </label>
              {videoFile && (
                <button
                  type="button"
                  onClick={clearVideo}
                  data-testid="admin-pm-clear-video"
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-navy/60 hover:text-red-600 px-3 py-2 rounded-full border border-transparent hover:border-red-200"
                >
                  <X className="w-3.5 h-3.5" /> Remover vídeo
                </button>
              )}
            </div>

            {videoError && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800" data-testid="admin-pm-video-error">
                {videoError}
              </div>
            )}

            {videoInfo && videoFile && (
              <div
                className={`rounded-xl p-4 border ${withinLimit ? "border-emerald-200 bg-emerald-50/50" : "border-red-300 bg-red-50/70"}`}
                data-testid="admin-pm-duration-panel"
              >
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-navy/50">Duração detectada</p>
                    <p className="font-medium text-navy" data-testid="admin-pm-duration-detected">{formatSeconds(videoInfo.duration)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-navy/50">Limite permitido</p>
                    <p className="font-medium text-navy">{formatSeconds(MAX_DURATION_S)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-navy/50">Status</p>
                    <p
                      className={`font-medium ${withinLimit ? "text-emerald-700" : "text-red-700"}`}
                      data-testid="admin-pm-duration-status"
                    >
                      {withinLimit ? "Dentro do limite" : "Acima do limite"}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-navy/60 mt-3">
                  Resolução: {videoInfo.width}×{videoInfo.height} · Tamanho: {bytesToMB(videoFile.size)} MB · Arquivo: {videoFile.name}
                </p>
                {!withinLimit && (
                  <p className="text-xs text-red-700 mt-2" data-testid="admin-pm-duration-error">
                    Este vídeo possui {formatSeconds(videoInfo.duration)}. O limite editorial da Palavra em 1 Minuto é de
                    {" "}{formatSeconds(MAX_DURATION_S)}. Edite a duração e tente novamente.
                  </p>
                )}
              </div>
            )}

            {preview && withinLimit && (
              <div className="rounded-xl border border-border bg-[hsl(var(--parchment))] p-3">
                <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55 mb-2">Pré-visualização</p>
                <video
                  src={preview}
                  controls
                  muted
                  playsInline
                  className="max-h-72 mx-auto rounded-md bg-black"
                  data-testid="admin-pm-preview-video"
                />
              </div>
            )}

            <label className="block">
              <span className="text-xs text-navy/70">Thumbnail (JPG/PNG, opcional)</span>
              <input
                ref={thumbRef}
                type="file"
                accept="image/*"
                onChange={(e) => setThumbFile(e.target.files?.[0] || null)}
                data-testid="admin-pm-thumb-input"
                className="mt-1 w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-navy/85 file:text-white file:px-3 file:py-1.5 file:text-[11px] file:uppercase file:tracking-widest"
              />
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-navy/70">
              Aceitamos links do <strong>YouTube</strong>, <strong>YouTube Shorts</strong> e URLs <strong>HTTPS</strong> que terminam em <strong>.mp4</strong>.
            </p>
            <label className="block">
              <span className="text-xs text-navy/70">URL do vídeo</span>
              <input
                type="url"
                inputMode="url"
                value={externalUrl}
                onChange={(e) => handleLinkChange(e.target.value)}
                placeholder="https://youtube.com/shorts/... ou https://.../video.mp4"
                data-testid="admin-pm-link-input"
                className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
            </label>
            {linkInfo && (
              <div
                className={`rounded-lg px-3 py-2 text-sm border ${
                  linkInfo.valid ? "border-emerald-200 bg-emerald-50/60 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"
                }`}
                data-testid="admin-pm-link-info"
              >
                {linkInfo.valid ? (
                  linkInfo.kind === "youtube" ? (
                    <>YouTube reconhecido · ID <span className="font-mono">{linkInfo.id}</span></>
                  ) : (
                    <>MP4 direto (HTTPS)</>
                  )
                ) : (
                  <>{linkInfo.error}</>
                )}
              </div>
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-border">
          <label className="block sm:col-span-2">
            <span className="text-xs text-navy/70">Título</span>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="admin-pm-title-input"
              required
              placeholder="Ex.: A paz que vem de Deus"
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-xs text-navy/70">Descrição curta</span>
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="admin-pm-description-input"
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
            />
          </label>
          <label className="block">
            <span className="text-xs text-navy/70">Data de publicação</span>
            <input
              type="date"
              value={form.scheduled_date}
              onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
              data-testid="admin-pm-date-input"
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
            />
            <span className="mt-1 block text-[10px] text-navy/50">
              A publicação é automática. No dia escolhido o vídeo aparece na Home.
            </span>
          </label>
          <label className="block">
            <span className="text-xs text-navy/70">Devocional (opcional)</span>
            <select
              value={form.devotional_id}
              onChange={(e) => {
                const id = e.target.value;
                const dev = devos.find((d) => d.id === id);
                setForm({
                  ...form,
                  devotional_id: id,
                  scheduled_date: dev?.date && /^\d{4}-\d{2}-\d{2}$/.test(dev.date) ? dev.date : form.scheduled_date,
                });
              }}
              data-testid="admin-pm-devotional-select"
              className="mt-1 w-full border border-border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gold/40"
            >
              <option value="">Sem vínculo</option>
              {devos.map((d) => (
                <option key={d.id} value={d.id}>
                  {(d.date ? `${d.date} · ` : "")}{d.title}{d.base_reference ? ` · ${d.base_reference}` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="pt-3 border-t border-border">
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="admin-pm-submit-btn"
            className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Enviando..." : "Salvar"}
          </button>
          <span className="ml-3 text-[11px] text-navy/50 uppercase tracking-widest align-middle">
            {source === SOURCE_UPLOAD ? "Upload será validado no servidor" : "Link será validado antes de salvar"}
          </span>
        </div>
      </form>

      {/* Lista */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="admin-pm-list">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.3em] text-navy/55">Vídeos cadastrados</p>
          <p className="text-xs text-navy/50">{items.length} {items.length === 1 ? "item" : "itens"}</p>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-navy/60">Carregando…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center space-y-2" data-testid="admin-pm-empty">
            <p className="text-sm text-navy/60">Ainda não existem vídeos cadastrados.</p>
            <p className="text-xs text-navy/50">Limite editorial: {formatSeconds(MAX_DURATION_S)} (89s).</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((it) => {
              const isLink = it.source_type === "external_url";
              return (
                <li key={it.id} data-testid={`admin-pm-item-${it.id}`} className="p-4 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                  {isLink ? (
                    <div className="w-full sm:w-24 h-48 sm:h-14 rounded-md border border-navy/10 bg-navy/5 flex items-center justify-center shrink-0" data-testid={`admin-pm-item-thumb-link-${it.id}`}>
                      {it.youtube_video_id ? (
                        <img
                          src={`https://img.youtube.com/vi/${it.youtube_video_id}/hqdefault.jpg`}
                          alt=""
                          className="w-full h-full object-cover rounded-md"
                        />
                      ) : (
                        <LinkIcon className="w-5 h-5 text-navy/40" />
                      )}
                    </div>
                  ) : (
                    <video
                      src={`${backend}${it.video_url}`}
                      muted
                      playsInline
                      preload="metadata"
                      className="w-full sm:w-24 h-48 sm:h-14 object-cover rounded-md border border-navy/10 bg-black shrink-0"
                      data-testid={`admin-pm-item-video-${it.id}`}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-navy break-words">{it.title}</p>
                    <div className="flex items-center gap-2 text-xs text-navy/60 mt-1.5 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLE[it.status] || ""}`} data-testid={`admin-pm-item-status-${it.id}`}>
                        {STATUS_LABEL[it.status] || it.status}
                      </span>
                      <span>{it.scheduled_date}</span>
                      {it.duration_seconds != null && <span>· {formatSeconds(it.duration_seconds)}</span>}
                      <span className="inline-flex items-center gap-1 text-navy/50">
                        {isLink ? <LinkIcon className="w-3 h-3" /> : <Upload className="w-3 h-3" />}
                        {isLink ? "link" : "upload"}
                      </span>
                    </div>
                    {isLink && it.external_url && (
                      <p className="text-xs text-navy/50 mt-1 truncate" data-testid={`admin-pm-item-url-${it.id}`}>
                        {it.external_url}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap sm:shrink-0 pt-2 border-t border-border sm:border-t-0 sm:pt-0">
                    <select
                      value={it.status}
                      onChange={(e) => changeStatus(it, e.target.value)}
                      data-testid={`admin-pm-status-select-${it.id}`}
                      className="text-xs border border-border rounded-lg px-2 py-2 min-h-[40px] bg-white flex-1 sm:flex-none"
                      title="Alterar status"
                    >
                      <option value="draft">Rascunho</option>
                      <option value="published">Publicado</option>
                      <option value="hidden">Oculto</option>
                    </select>
                    {!isLink && (
                      <button
                        type="button"
                        onClick={() => setReplacingId(it.id)}
                        data-testid={`admin-pm-replace-btn-${it.id}`}
                        className="min-h-[40px] min-w-[40px] rounded-full border border-navy/25 text-navy/70 hover:bg-navy/5 flex items-center justify-center"
                        title="Substituir arquivo"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(it.id)}
                      data-testid={`admin-pm-delete-btn-${it.id}`}
                      className="min-h-[40px] min-w-[40px] rounded-full border border-rose-300 text-rose-600 hover:bg-rose-50 flex items-center justify-center ml-auto sm:ml-0"
                      title="Excluir"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Modal Replace */}
      {replacingId && (
        <div className="fixed inset-0 bg-navy/60 z-50 flex items-center justify-center p-4" data-testid="admin-pm-replace-modal" onClick={() => setReplacingId(null)}>
          <div className="bg-white rounded-2xl border border-border max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <p className="font-display text-lg text-navy">Substituir arquivo</p>
            <p className="text-sm text-navy/70 mt-2">
              Envie um novo MP4 ou MOV (≤{formatSeconds(MAX_DURATION_S)}, ≤{MAX_SIZE_BYTES / (1024*1024)}MB).
              O arquivo anterior é marcado como órfão e o link público aponta para o novo.
            </p>
            <input
              ref={replaceRef}
              type="file"
              accept={VIDEO_ACCEPT}
              data-testid="admin-pm-replace-input"
              className="mt-4 w-full text-sm"
            />
            <div className="flex items-center justify-end gap-2 mt-5">
              <button type="button" onClick={() => setReplacingId(null)} data-testid="admin-pm-replace-cancel" className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest border border-border text-navy/70">Cancelar</button>
              <button type="button" onClick={() => doReplace(replacingId, replaceRef.current?.files?.[0])} data-testid="admin-pm-replace-confirm" className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest bg-navy text-white hover:bg-gold hover:text-navy">
                Substituir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Delete */}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-navy/60 z-50 flex items-center justify-center p-4" data-testid="admin-pm-delete-modal" onClick={() => setConfirmDeleteId(null)}>
          <div className="bg-white rounded-2xl border border-border max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <p className="font-display text-lg text-navy">Excluir vídeo?</p>
            <p className="text-sm text-navy/70 mt-2">
              O registro será marcado como excluído e o arquivo removido da lista pública.
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button type="button" onClick={() => setConfirmDeleteId(null)} data-testid="admin-pm-delete-cancel" className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest border border-border text-navy/70">Cancelar</button>
              <button type="button" onClick={() => doDelete(confirmDeleteId)} data-testid="admin-pm-delete-confirm" className="px-4 py-2 rounded-full text-[11px] uppercase tracking-widest bg-rose-600 text-white hover:bg-rose-700">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
